import Table, { Table$RowSelectionChangeEvent, Table$SortEvent } from "sap/ui/table/Table";
import MultiSelectionPlugin, { MultiSelectionPlugin$SelectionChangeEvent } from "sap/ui/table/plugins/MultiSelectionPlugin";
import JSONModel from "sap/ui/model/json/JSONModel";
import Filter from "sap/ui/model/Filter";
import ODataListBinding, { ODataListBinding$DataReceivedEvent } from "sap/ui/model/odata/v4/ODataListBinding";
import Context from "sap/ui/model/Context";
import Sorter from "sap/ui/model/Sorter";
import ListBinding from "sap/ui/model/ListBinding";
import ManagedObject, { AggregationBindingInfo } from "sap/ui/base/ManagedObject";
import JSONFilterFieldDelegate from "vamos/cockpit/base/delegates/JSONFilterFieldDelegate";
import VariantManagementService from "vamos/cockpit/base/services/VariantManagementService";
import BaseController from "vamos/cockpit/base/controller/type/BaseController";

/**
 * TODO move Code from {@link webapp/base/services/ListService.js}
 * */
export default class ListHandler {
    private table: Table;
    private viewModel: JSONModel;
    private tableModel: JSONModel;

    constructor(table: Table, viewModel: JSONModel, tableModel: JSONModel) {
        this.table = table;
        this.viewModel = viewModel;
        this.viewModel.setProperty(`/${this.getTableId()}`, {});

        this.tableModel = tableModel;

        table.attachRowSelectionChange(this.onSelectionChange, this);
    }

    bindTable(entitySet: string, parameters: object = {}, filters: Filter[] = [], sorter?: Sorter): void {
        const bindingInfo:AggregationBindingInfo =  this.getBindingInfo(entitySet, parameters, filters, sorter);
        this.table.bindRows(bindingInfo);
    }

    public getBindingInfo(entitySet: string, parameters: object = {}, filters: Filter[] = [], sorter?: Sorter):AggregationBindingInfo {
        const sPath = entitySet.match("Detail") ? entitySet : "/" + entitySet + "s";

        const oActiveColumnFilters = this.getActiveColumnFilter();
        if (oActiveColumnFilters) {
            filters.push(oActiveColumnFilters);
        }
        return {
            path: sPath,
            filters: filters.length > 0 ? new Filter(filters, true) : [],
            sorter: sorter,
            parameters: { $count: true, ...parameters },
            events: {
                dataReceived: (e: ODataListBinding$DataReceivedEvent) => this.onDataReceived(e),
            },
        };
    }

    private getActiveColumnFilter(): Filter | null {
        const filters: Filter[] = [];

        // todo test if that works
        // this.table.getColumns().forEach((oColumn: Column) => {
        //   const oMenu = Element.getElementById(oColumn.getHeaderMenu()) as ColumnMenu;
        //   if (oMenu) {
        //     const oFilterItem = oMenu.getItems().find(item => item instanceof FilterItem) as FilterItem;
        //     if (oFilterItem && oFilterItem.getProperty("filterValue")) {
        //       filters.push(new Filter(
        //         oColumn.getFilterProperty(),
        //         FilterOperator.Contains,
        //         oFilterItem.getProperty("filterValue")
        //       ));
        //     }
        //   }
        // });

        // todo find better Implementation
        this.table.getColumns().forEach((oColumn) => {
            //@ts-ignore
            const oColumnFilter = oColumn._getFilter();
            if (oColumnFilter) {
                filters.push(oColumnFilter);
            }
        });
        return filters.length > 0 ? new Filter(filters, true) : null;
    }

    public getSelectedContext(): Context[] {
        const table = this.table;
        let aSelectedIndices = ListHandler.getSelectedTableIndices(table);
        return aSelectedIndices.map((iIndex) => {
            return table.getContextByIndex(iIndex);
        });
    }

    public static getSelectedTableIndices(oTable?: Table) {
        let arSelectedIndices: int[] = [];
        const aggregations = oTable.getAggregation("dependents");
        let isMultiSelection = false;
        if (Array.isArray(aggregations) && aggregations.length > 0) {
            for (let i = 0; i < aggregations.length; i++) {
                const managedObject = aggregations[i];
                if (managedObject instanceof MultiSelectionPlugin) {
                    arSelectedIndices = managedObject.getSelectedIndices();
                    isMultiSelection = true;
                    break;
                }
            }
        }
        if (!isMultiSelection) {
            arSelectedIndices = oTable.getSelectedIndices();
        }
        return arSelectedIndices;
    }

    private getTableId(): string {
        return this.table.getId().split("--").pop();
    }

    private onDataReceived(oEvent: ODataListBinding$DataReceivedEvent) {
        this.viewModel.setProperty(`/${this.getTableId()}/count`, oEvent.getSource().getLength());
    };

    onSelectionChange(event: Table$RowSelectionChangeEvent) {
        const selectionCount: int = this.table.getSelectedIndices().length;
        this.setCount(selectionCount);
    };

    public onSort(event: Table$SortEvent, controller?: BaseController): void {
        event.preventDefault();
        const column = event.getParameter("column");

        let sorter: Sorter;
        if (event.getSource() instanceof Table) {
            column.setSortOrder(event.getParameter("sortOrder"));
            const key = column.getProperty("sortProperty");
            sorter = new Sorter(key, event.getParameter("sortOrder") === "Descending");
            this.tableModel.setProperty(`/${this.getTableId()}/sorters/`, {
                [key]: event.getParameter("sortOrder"),
            });
            // user sorted a column -> show "save" on the variant management
            if (controller) {
                VariantManagementService.markModified.call(controller, this.getTableId(), true);
            }

        } else {
            sorter = JSONFilterFieldDelegate.getSorterFromTableModel.call(controller, this.tableModel, this.table);
        }

        const oBinding = this.table.getBinding("rows") as ListBinding;
        if (oBinding) {
            oBinding.sort(sorter);
        }
    }

    public async scrollTo(targetGuid: string) {
        const oTable = this.table;

        const binding = oTable.getBinding("rows") as ListBinding;
        if (!(binding instanceof ODataListBinding)) {
            throw new Error("Binding is not an ODataListBinding");
        }

        let bFound = false;
        let currentItems = binding?.getAllCurrentContexts().length;
        let maxItems = binding.getLength();
        let firstVisibleRow = oTable.getFirstVisibleRow();
        let visibleRows = oTable.getVisibleRowCount();


        (binding as ODataListBinding).getAllCurrentContexts().forEach((oRowContext, iIndex) => {
            if (oRowContext.getObject().ProductGuid === targetGuid) {
                let halfVisibleRows = Math.floor(visibleRows / 2);
                oTable.setFirstVisibleRow(Math.max(0, iIndex - halfVisibleRows));

                const plugin = this.getMultiSelectionPlugin();
                if (plugin) {
                    plugin.setSelectedIndex(iIndex);
                } else {
                    oTable.setSelectedIndex(iIndex);
                }
                bFound = true;
            }
        });
        if (!bFound && currentItems < maxItems) {
            let callCount = 100;
            if (maxItems - currentItems < 100) callCount = maxItems - currentItems;
            await (oTable.getBinding("rows") as ODataListBinding).requestContexts(currentItems, callCount);
            await this.scrollTo(targetGuid);
        }
    }

    private getMultiSelectionPlugin(): null | MultiSelectionPlugin {
        const aggregations = this.table.getAggregation("dependents");
        if (aggregations == null) {
            return null;
        }

        if (!Array.isArray(aggregations) && aggregations instanceof MultiSelectionPlugin) {
            return aggregations;
        }

        return (aggregations as ManagedObject[]).find(managedObject => managedObject instanceof MultiSelectionPlugin);
    }
    public onSelectionChangePlugin(event: MultiSelectionPlugin$SelectionChangeEvent){
        const plugin:MultiSelectionPlugin = event.getSource();
        const indices = plugin.getSelectedIndices();
        this.setCount(indices.length);

    }
    public setCount(count:number){
        this.viewModel.setProperty(`/${this.getTableId()}/selectedCount`, count);

    }

}